import {
  ConstructionCategory,
  type ConstructionCategoryValue,
  type ConstructionDefinition,
  getConstructionsByCategory,
  type ObjectTypeValue,
  type TranslationKey,
} from '@dreamgame/shared';
import { useEffect, useRef } from 'preact/hooks';
import { tv } from 'tailwind-variants';
import { getObjectTexture } from '../../render/layers/ObjectLayer';
import {
  activeCategory,
  closeConstructionPanel,
  isConstructionPanelOpen,
  isPlacingConstruction,
  selectConstruction,
  selectedConstruction,
  setActiveCategory,
} from '../../state/construction';
import { t } from '../../state/i18n';

// ============================================================================
// ConstructionPanel - Fantasy Medieval Crafting Tome UI
// ============================================================================

// Styles using tailwind-variants - Fantasy Medieval Theme
const panel = tv({
  slots: {
    // Positioned slightly higher to account for side tabs
    root: [
      'fixed left-[1.1em] bottom-[6.2em]',
      'z-[1000]',
      'flex items-start',
      'animate-fade-in',
      'text-[16px]',
      'subpixel-antialiased',
    ],

    // The Book Spine (Vertical bar between tabs and page)
    spine: [
      'w-[1.2em] h-[21.2em]', // Slightly taller than page
      'bg-gradient-to-r from-leather-dark via-leather to-leather-dark',
      'border-y-2 border-l-2 border-leather-border',
      'rounded-l-md',
      'shadow-xl',
      'z-20',
      'relative',
    ],

    // The Main Page (Right side)
    container: [
      'relative',
      'w-[20.5em] h-[20em]',
      'bg-parchment', // Base color
      // Complex paper texture effect
      'bg-[radial-gradient(ellipse_at_center,_var(--color-parchment-light)_0%,_var(--color-parchment)_100%)]',
      'border-y-4 border-r-4 border-leather', // Leather cover edges
      'rounded-r-lg',
      'shadow-tome',
      'flex flex-col',
      'z-10',
      // Inner page border
      'after:content-[""]',
      'after:absolute after:inset-[0.45em]',
      'after:border-2 after:border-gold-filigree/30',
      'after:rounded-r-md',
      'after:pointer-events-none',
    ],

    // Decorative corner flourishes (CSS only)
    cornerDecorTop: [
      'absolute top-[0.55em] right-[0.55em] w-[2em] h-[2em]',
      'border-t-2 border-r-2 border-gold-filigree',
      'rounded-tr-lg',
      'pointer-events-none',
    ],
    cornerDecorBottom: [
      'absolute bottom-[0.55em] right-[0.55em] w-[2em] h-[2em]',
      'border-b-2 border-r-2 border-gold-filigree',
      'rounded-br-lg',
      'pointer-events-none',
    ],

    // Header within the page
    header: [
      'relative',
      'flex items-center justify-center',
      'py-[1em]',
      'mx-[1.5em] mt-[0.55em]',
      'border-b border-gold-filigree/50',
    ],

    title: ['font-fantasy text-ink text-[18px] tracking-widest', 'uppercase', 'font-bold'],

    closeButton: [
      'absolute -right-[0.65em] -top-[0.65em]',
      'w-[2.15em] h-[2.15em]',
      'flex items-center justify-center',
      'rounded-full',
      'bg-leather-dark',
      'border-2 border-gold-filigree',
      'text-gold-bright',
      'hover:scale-110',
      'hover:bg-wax-seal',
      'transition-all duration-200',
      'cursor-pointer',
      'shadow-lg',
      'z-50',
    ],

    // Side Tabs (Bookmarks)
    tabContainer: [
      'flex flex-col gap-[0.4em]',
      'mt-[2.6em]',
      '-mr-[0.5em]', // Overlap spine slightly
      'z-0',
    ],

    tab: [
      'w-[9.6em]', // Wide enough to stick out
      'h-[2.95em]',
      'pl-[1em] pr-[1.25em]', // Padding for text
      'flex items-center justify-between',
      'bg-leather',
      'border-y border-l border-gold-filigree',
      'rounded-l-full', // Rounded bookmark end
      'text-[12px] font-fantasy tracking-wider font-bold',
      'text-parchment-dark',
      'shadow-md',
      'transition-all duration-200',
      'cursor-pointer',
      'hover:translate-x-[-4px]', // Slide out on hover
      'relative',
    ],

    tabActive: [
      'bg-leather-light',
      'text-gold-bright',
      'border-gold-bright',
      'translate-x-[12px]', // Stick out when active
      'z-30', // Above spine
      'shadow-lg',
    ],

    tabIcon: ['w-[1em] h-[1em]', 'opacity-70'],
    tabLabel: ['flex-1', 'text-center'],

    // Content area
    content: [
      'flex-1',
      'p-[1.45em]',
      'overflow-y-auto',
      'scrollbar-thin scrollbar-thumb-leather scrollbar-track-transparent',
    ],

    grid: ['grid grid-cols-2 gap-[0.55em] auto-rows-min'],

    // Item cards - Hand-drawn look
    itemCard: [
      'flex flex-col items-center justify-center',
      'p-[0.45em]',
      'h-auto min-h-[6.2em]', // Let content dictate height but keep minimum
      'rounded-md',
      'border border-transparent', // Invisible border normally
      'hover:bg-ink/5',
      'transition-all duration-200',
      'cursor-pointer',
      'group',
    ],

    itemCardSelected: ['bg-ink/5', 'border-gold-filigree', 'shadow-sm'],

    // Image placeholder for item
    itemImage: ['w-[3.05em] h-[3.05em]', 'mb-[0.25em]', 'flex items-center justify-center'],

    itemName: ['text-ink text-[12px] font-bold text-center', 'font-medieval', 'leading-tight'],

    itemCost: ['text-[11px] text-ink-faded', 'font-mono', 'text-center', 'leading-none'],

    placementHint: [
      'absolute bottom-[0.95em] left-[1.45em] right-[1.45em]',
      'text-[11px] text-ink-faded italic text-center',
      'animate-pulse',
    ],
  },
});

// Tab configuration with Icons (simulated)
const TABS: ReadonlyArray<{
  id: ConstructionCategoryValue;
  labelKey: TranslationKey;
  icon: string;
}> = [
  { id: ConstructionCategory.STRUCTURE, labelKey: 'ui.tabs.structure', icon: '🏰' },
  { id: ConstructionCategory.FURNITURE, labelKey: 'ui.tabs.furniture', icon: '🪑' },
  { id: ConstructionCategory.WORKSTATION, labelKey: 'ui.tabs.work', icon: '⚒️' },
  { id: ConstructionCategory.FLOOR, labelKey: 'ui.tabs.floor', icon: '🪵' },
];

const ConstructionIcon = ({ objectType }: { objectType: ObjectTypeValue }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const texture = getObjectTexture({ objectType });
    if (!texture) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Render texture to canvas
    // Pixi 8 access pattern
    const source = texture.source;
    const image = (source as { resource?: HTMLImageElement }).resource;

    if (image) {
      const { x, y, width, height } = texture.frame;
      ctx.drawImage(image, x, y, width, height, 0, 0, canvas.width, canvas.height);
    }
  }, [objectType]);

  return (
    <canvas ref={canvasRef} width={64} height={64} class="w-[2.6em] h-[2.6em] drop-shadow-md" />
  );
};

const ConstructionItem = ({
  definition,
  isSelected,
  onSelect,
}: {
  definition: ConstructionDefinition;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const styles = panel();

  return (
    <button
      type="button"
      class={`${styles.itemCard()} ${isSelected ? styles.itemCardSelected() : ''}`}
      onClick={onSelect}
    >
      <div class={styles.itemImage()}>
        <ConstructionIcon objectType={definition.objectType} />
      </div>
      <span class={styles.itemName()}>{definition.name}</span>
      <div class={styles.itemCost()}>
        {definition.buildCost.map((cost, idx) => (
          <div key={idx}>
            {cost.amount} {cost.resourceType}
          </div>
        ))}
      </div>
    </button>
  );
};

export const ConstructionPanel = () => {
  const styles = panel();

  if (!isConstructionPanelOpen.value) {
    return null;
  }

  const currentCategory = activeCategory.value;
  const constructions = getConstructionsByCategory({ category: currentCategory });
  const selected = selectedConstruction.value;
  const isPlacing = isPlacingConstruction.value;

  const handleTabClick = ({ category }: { category: ConstructionCategoryValue }) => {
    setActiveCategory({ category });
  };

  const handleItemSelect = ({ definition }: { definition: ConstructionDefinition }) => {
    if (selected?.objectType === definition.objectType) {
      selectConstruction({ definition: null });
    } else {
      selectConstruction({ definition });
    }
  };

  const handleClose = () => {
    closeConstructionPanel();
  };

  return (
    <div class={styles.root()}>
      {/* Left Tabs (Bookmarks) */}
      <div class={styles.tabContainer()}>
        {TABS.map((tab) => (
          <button
            type="button"
            key={tab.id}
            class={`${styles.tab()} ${currentCategory === tab.id ? styles.tabActive() : ''}`}
            onClick={() => handleTabClick({ category: tab.id })}
          >
            {/* Decorative gold tip on left */}
            <div class="absolute left-[0.25em] top-1/2 -translate-y-1/2 w-[0.12em] h-[2.1em] border-l border-gold-filigree opacity-50" />

            <span class={styles.tabLabel()}>{t(tab.labelKey)}</span>
            <span class={styles.tabIcon()}>{tab.icon}</span>
          </button>
        ))}
      </div>

      {/* Book Spine */}
      <div class={styles.spine()} />

      {/* Main Page */}
      <div class={styles.container()}>
        {/* Close Button (Metal clasp style) */}
        <button type="button" class={styles.closeButton()} onClick={handleClose} aria-label="Close">
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            class="w-[1em] h-[1em]"
            role="img"
            aria-hidden="true"
          >
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
          </svg>
        </button>

        {/* Decorative Corners */}
        <div class={styles.cornerDecorTop()} />
        <div class={styles.cornerDecorBottom()} />

        {/* Page Header */}
        <div class={styles.header()}>
          <h2 class={styles.title()}>{t('ui.panels.build')}</h2>
        </div>

        {/* Grid Content */}
        <div class={styles.content()}>
          <div class={styles.grid()}>
            {constructions.map((def) => (
              <ConstructionItem
                key={def.objectType}
                definition={def}
                isSelected={selected?.objectType === def.objectType}
                onSelect={() => handleItemSelect({ definition: def })}
              />
            ))}
          </div>
        </div>

        {/* Bottom Hint Text */}
        {isPlacing && selected && (
          <div class={styles.placementHint()}>{t('ui.tooltips.placementHint')}</div>
        )}
      </div>
    </div>
  );
};
